import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Nav } from '../components/Nav';
import { Footer } from '../components/Footer';
import { UserSearch, Mail, Bell, Workflow, Sparkles, Inbox, Layout, FileInput, ClipboardList, Share2, Star, KanbanSquare, CalendarCheck, BarChart3, Globe, MessageCircle, AlertTriangle, UserX, Database, HelpCircle, CheckCircle2, XCircle, ArrowRight, Plug, Gift } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const features = [{
  icon: <UserSearch size={22} data-id="element-2508" />,
  title: 'Contact Enrichment',
  desc: 'Someone adds a name and email? MR finds their phone, company, social profiles, and more — automatically filling in the blanks.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Mail size={22} data-id="element-2509" />,
  title: 'Email Campaigns',
  desc: 'MR writes, designs, and sends email campaigns in your brand voice. It even tests different versions to see what works best.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Bell size={22} data-id="element-2510" />,
  title: 'Smart Follow-Ups',
  desc: 'Never miss a follow-up again. MR sends the right message at the right time based on what each contact is doing.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Workflow size={22} data-id="element-2511" />,
  title: 'Automation Workflows',
  desc: 'When a lead fills out a form, MR can send a welcome email, assign a task, update the pipeline, and notify your team — all automatically.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Sparkles size={22} data-id="element-2512" />,
  title: 'Data Cleanup',
  desc: 'Imported a messy spreadsheet? MR fixes duplicate contacts, fills in missing fields, and organizes everything perfectly.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Inbox size={22} data-id="element-2513" />,
  title: 'Email Deliverability',
  desc: 'MR makes sure your emails actually land in inboxes — not spam folders. All the behind-the-scenes setup is handled for you.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Layout size={22} data-id="element-2514" />,
  title: 'Landing Pages',
  desc: 'Need a page for your next promotion? MR builds beautiful landing pages that match your brand — no designer needed.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <FileInput size={22} data-id="element-2515" />,
  title: 'Web Forms',
  desc: 'Create contact forms, signup forms, and lead capture forms that feed directly into your CRM — automatically.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <ClipboardList size={22} data-id="element-2516" />,
  title: 'Surveys & Quizzes',
  desc: 'Engage your audience with interactive surveys and quizzes that capture leads and learn what your customers want.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Share2 size={22} data-id="element-2517" />,
  title: 'Social Media Posting',
  desc: 'MR schedules and posts to your social channels, keeping your brand active and visible without you thinking about it.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Star size={22} data-id="element-2518" />,
  title: 'Review Management',
  desc: 'MR asks happy customers for reviews and responds to them — building your reputation on autopilot.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <KanbanSquare size={22} data-id="element-2519" />,
  title: 'Pipeline Management',
  desc: 'See exactly where every deal stands. MR moves deals through your pipeline based on real activity — no manual dragging.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <CalendarCheck size={22} data-id="element-2520" />,
  title: 'Appointment Scheduling',
  desc: 'Let leads book time with you directly. MR handles the calendar, sends reminders, and follows up if they no-show.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <BarChart3 size={22} data-id="element-2521" />,
  title: 'Reporting & Insights',
  desc: "Know exactly what's working. MR generates clear reports on your leads, deals, campaigns, and revenue.",
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Globe size={22} data-id="element-2522" />,
  title: 'Domain Management',
  desc: 'MR handles all the technical setup for your sending domains so your emails look professional and trustworthy.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <MessageCircle size={22} data-id="element-2523" />,
  title: 'SMS & Text Messaging',
  desc: 'Reach contacts instantly with text messages — appointment reminders, promotions, or quick follow-ups.',
  color: 'bg-purple/10 text-purple'
}];
const painPoints = [{
  icon: <Database size={28} data-id="element-2524" />,
  title: 'Contacts With Missing Info',
  desc: "Half your contacts are just a name and email. No phone number, no company, no notes. You're flying blind every time you reach out."
}, {
  icon: <UserX size={28} data-id="element-2525" />,
  title: 'Follow-Ups That Slip Away',
  desc: 'You added a lead last week but forgot to follow up. Now they went with someone else. Sound familiar?'
}, {
  icon: <AlertTriangle size={28} data-id="element-2526" />,
  title: 'Hours Cleaning Up Messy Data',
  desc: 'You imported a list and now there are duplicates everywhere, fields in the wrong places, and half the emails are bouncing.'
}, {
  icon: <HelpCircle size={28} data-id="element-2527" />,
  title: 'No Idea Which Leads Are Hot',
  desc: "You've got 500 contacts but no clue who's ready to buy. Everything looks the same and nothing is prioritized."
}];
const crmLogos = [{
  name: 'HubSpot',
  domain: 'hubspot.com'
}, {
  name: 'Salesforce',
  domain: 'salesforce.com'
}, {
  name: 'Keap',
  domain: 'keap.com'
}, {
  name: 'Pipedrive',
  domain: 'pipedrive.com'
}, {
  name: 'Zoho',
  domain: 'zoho.com'
}, {
  name: 'Monday.com',
  domain: 'monday.com'
}, {
  name: 'Freshsales',
  domain: 'freshworks.com'
}, {
  name: 'Copper',
  domain: 'copper.com'
}];
const dashBenefits = ['Unlimited users — no per-seat fees', 'Built-in email & SMS marketing', 'Landing pages & funnels included', 'Appointment scheduling & calendars', 'Reputation & review management', 'Full automation & workflow builder'];
const withoutMr = ['Contacts missing phone numbers and details', 'Follow-ups forgotten and leads lost', 'Hours spent on manual data entry', 'Messy imports with duplicates everywhere', 'Emails landing in spam folders', 'No idea which leads are ready to buy', "Paying for 5 different tools that don't talk to each other"];
const withMr = ['Every contact automatically enriched with full details', 'Follow-ups sent at the perfect time, every time', 'Zero manual data entry — MR handles it all', 'Imports cleaned and organized instantly', 'Emails delivered to inboxes, not spam', 'Hot leads flagged and prioritized for you', 'One system that does everything — included'];
export function SelfManagingCrm() {
  return <div className="w-full bg-white" data-id="element-2528">
      <Nav data-id="element-2529" />

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative bg-[#0F1D3A] pt-44 pb-24 px-6 overflow-hidden" data-id="element-2530">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple/15 rounded-full blur-[120px] pointer-events-none" data-id="element-2531" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal/10 rounded-full blur-[100px] pointer-events-none" data-id="element-2532" />
        <div className="max-w-5xl mx-auto relative z-10 text-center" data-id="element-2533">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-2534">
            <motion.span variants={fadeUp} className="inline-block text-teal text-sm font-bold tracking-widest uppercase mb-4" data-id="element-2535">
              Self-Running CRM
            </motion.span>
            <motion.h1 variants={fadeUp} className="font-poppins font-extrabold text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6" data-id="element-2536">
              Your CRM. <br className="hidden md:block" data-id="element-2537" />
              <span className="text-gradient" data-id="element-2538">Finally Running Itself.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-2539">
              No more stale contacts, missed follow-ups, or messy data. Just
              tell MR what you need — and your entire customer pipeline stays
              perfect without you lifting a finger.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-2540">
              <Link to="/contact" className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow" data-id="element-2541">
                Talk to MR
              </Link>
              <Link to="/how-it-works" className="btn-ghost hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-shadow" data-id="element-2542">
                See It In Action
              </Link>
            </motion.div>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-6" data-id="element-2543">
              {[{
              value: '90+',
              label: 'Integrations'
            }, {
              value: '∞',
              label: 'Unlimited Users'
            }, {
              value: '$5,995',
              label: 'Migration — Free'
            }].map(stat => <div key={stat.label} className="bg-white/5 border border-white/10 rounded-xl px-6 py-3 text-center" data-id="element-2544">
                  <div className="text-2xl font-bold text-white" data-id="element-2545">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-400 font-medium" data-id="element-2546">
                    {stat.label}
                  </div>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ PAIN SECTION ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2547">
        <div className="max-w-6xl mx-auto" data-id="element-2548">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2549">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2550">
              Sound Familiar?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2551">
              If any of these hit close to home, you're not alone. Most
              businesses deal with this every single day.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid md:grid-cols-2 gap-6" data-id="element-2552">
            {painPoints.map(p => <motion.div key={p.title} variants={fadeUp} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2553">
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 mb-4" data-id="element-2554">
                  {p.icon}
                </div>
                <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-2" data-id="element-2555">
                  {p.title}
                </h3>
                <p className="text-gray-500 leading-relaxed" data-id="element-2556">{p.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ WORKS WITH ANY CRM ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2557">
        <div className="max-w-5xl mx-auto" data-id="element-2558">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2559">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-teal/10 text-teal px-4 py-1.5 rounded-full text-sm font-semibold mb-4" data-id="element-2560">
              <Plug size={16} data-id="element-2561" /> Works With Any CRM
            </motion.div>
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2562">
              Or Integrate What You Already Love.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2563">
              Already have a CRM you love? MR plugs right in and makes it
              better. No switching required. No changing how you work. Just
              better results.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid grid-cols-2 sm:grid-cols-4 gap-6" data-id="element-2564">
            {crmLogos.map(crm => <motion.div key={crm.name} variants={fadeUp} className="bg-gray-50 rounded-xl p-5 flex flex-col items-center gap-3 border border-gray-100 hover:border-teal/30 hover:shadow-md transition-all" data-id="element-2565">
                <img src={`https://logo.clearbit.com/${crm.domain}`} alt={crm.name} className="w-10 h-10 rounded-lg object-contain" onError={e => {
              const target = e.target as HTMLImageElement;
              target.src = `https://www.google.com/s2/favicons?domain=${crm.domain}&sz=128`;
            }} data-id="element-2566" />
                <span className="text-sm font-semibold text-gray-700" data-id="element-2567">
                  {crm.name}
                </span>
              </motion.div>)}
          </motion.div>
          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-center text-gray-400 text-sm mt-6" data-id="element-2568">
            + dozens more. If your CRM has a way to connect, MR can work with
            it.
          </motion.p>
        </div>
      </section>

      {/* ═══════════════ 1DASH PRO INCLUDED ═══════════════ */}
      <section className="py-20 px-6 bg-[#0F1D3A]" data-id="element-2569">
        <div className="max-w-5xl mx-auto" data-id="element-2570">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2571">
            <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-2572">
              <span className="inline-flex items-center gap-2 bg-purple/10 text-purple px-4 py-1.5 rounded-full text-sm font-semibold mb-4" data-id="element-2573">
                <Gift size={16} data-id="element-2574" /> Included With Your Complete Package
              </span>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4" data-id="element-2575">
                Get Your CRM Included —{' '}
                <span className="text-gradient" data-id="element-2576">Standard.</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-2" data-id="element-2577">
                Don't have a CRM yet? No problem. When you go with our complete
                package, you get{' '}
                <strong className="text-white" data-id="element-2578">1Dash Pro</strong> — a
                full-featured CRM with unlimited users, included at no extra
                cost. No per-seat fees. No surprises.
              </p>
              <p className="text-gray-500 text-sm" data-id="element-2579">
                Already have a CRM? That's totally fine — this is an option, not
                a requirement.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-10" data-id="element-2580">
              <div className="flex flex-col md:flex-row items-center gap-6 mb-8" data-id="element-2581">
                <div className="flex items-center gap-3" data-id="element-2582">
                  <div className="w-12 h-12 rounded-xl bg-purple/20 flex items-center justify-center" data-id="element-2583">
                    <Database size={24} className="text-purple" data-id="element-2584" />
                  </div>
                  <div data-id="element-2585">
                    <h3 className="text-xl font-poppins font-bold text-white" data-id="element-2586">
                      1Dash Pro
                    </h3>
                    <span className="text-xs text-gray-400 bg-white/5 px-2 py-0.5 rounded-full" data-id="element-2587">
                      Powered by GoHighLevel
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2588">
                {dashBenefits.map(b => <div key={b} className="flex items-start gap-3" data-id="element-2589">
                    <CheckCircle2 size={18} className="text-teal shrink-0 mt-0.5" data-id="element-2590" />
                    <span className="text-gray-300 text-sm" data-id="element-2591">{b}</span>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ WHAT MR HANDLES ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2592">
        <div className="max-w-6xl mx-auto" data-id="element-2593">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2594">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2595">
              Everything MR Handles For You
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2596">
              All the stuff that usually falls through the cracks, stresses you
              out, or takes hours of your day — MR takes care of it.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-2597">
            {features.map(f => <motion.div key={f.title} variants={fadeUp} className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all group" data-id="element-2598">
                <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-2599">
                  {f.icon}
                </div>
                <h3 className="font-poppins font-semibold text-gray-900 mb-2" data-id="element-2600">
                  {f.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-2601">
                  {f.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ MIGRATION OFFER ═══════════════ */}
      <section className="py-16 px-6" data-id="element-2602">
        <div className="max-w-5xl mx-auto" data-id="element-2603">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={fadeUp} className="relative rounded-2xl overflow-hidden" data-id="element-2604">
            <div className="absolute inset-0 bg-gradient-to-r from-purple to-teal opacity-90" data-id="element-2605" />
            <div className="relative z-10 p-10 md:p-14 text-center text-white" data-id="element-2606">
              <span className="inline-block bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-semibold mb-4" data-id="element-2607">
                Limited Time Offer
              </span>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl mb-4" data-id="element-2608">
                Switching CRMs? We'll Move Everything — Free.
              </h2>
              <p className="text-white/85 text-lg max-w-2xl mx-auto mb-8 leading-relaxed" data-id="element-2609">
                Normally a{' '}
                <strong className="text-white" data-id="element-2610">$5,995 service</strong>. We'll
                migrate all your contacts, lists, automations, and data from
                your old CRM to 1Dash Pro at no cost. Zero downtime, zero
                hassle.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-purple font-semibold px-8 py-3.5 rounded-lg hover:bg-white/90 transition-colors shadow-lg" data-id="element-2611">
                Claim Your Free Migration <ArrowRight size={18} data-id="element-2612" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ BEFORE & AFTER ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2613">
        <div className="max-w-5xl mx-auto" data-id="element-2614">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2615">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2616">
              The Difference Is Night and Day
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid md:grid-cols-2 gap-8" data-id="element-2617">
            {/* Without MR */}
            <motion.div variants={fadeUp} className="bg-red-50 border border-red-200 rounded-2xl p-8" data-id="element-2618">
              <h3 className="font-poppins font-bold text-xl text-red-700 mb-6 flex items-center gap-2" data-id="element-2619">
                <XCircle size={24} data-id="element-2620" /> Without MR
              </h3>
              <ul className="space-y-4" data-id="element-2621">
                {withoutMr.map(item => <li key={item} className="flex items-start gap-3 text-red-700/80" data-id="element-2622">
                    <XCircle size={16} className="shrink-0 mt-1 text-red-400" data-id="element-2623" />
                    <span className="text-sm leading-relaxed" data-id="element-2624">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
            {/* With MR */}
            <motion.div variants={fadeUp} className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8" data-id="element-2625">
              <h3 className="font-poppins font-bold text-xl text-emerald-700 mb-6 flex items-center gap-2" data-id="element-2626">
                <CheckCircle2 size={24} data-id="element-2627" /> With MR
              </h3>
              <ul className="space-y-4" data-id="element-2628">
                {withMr.map(item => <li key={item} className="flex items-start gap-3 text-emerald-700/80" data-id="element-2629">
                    <CheckCircle2 size={16} className="shrink-0 mt-1 text-emerald-500" data-id="element-2630" />
                    <span className="text-sm leading-relaxed" data-id="element-2631">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ FINAL CTA ═══════════════ */}
      <section className="py-24 px-6 bg-[#0F1D3A] text-center" data-id="element-2632">
        <div className="max-w-3xl mx-auto" data-id="element-2633">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2634">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-white mb-6" data-id="element-2635">
              Ready to Stop Babysitting Your CRM?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-400 text-lg mb-10 max-w-xl mx-auto" data-id="element-2636">
              Let MR handle the details that drive you crazy — so you can focus
              on growing your business.
            </motion.p>
            <motion.div variants={fadeUp} data-id="element-2637">
              <Link to="/contact" className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow text-lg px-10 py-4" data-id="element-2638">
                Talk to MR
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-2639" />
    </div>;
}